import { useEffect, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { UserType } from "@/shared/types/user.entity";

import { InternalApiKeysService } from "../services/internalApiKeys.service";
import type { InternalApiKeyAccess } from "../types/internalApiKeys.types";

/** Waits between new attempts after `/access` fails, while something that asks stays mounted. */
const RETRY_DELAYS_MS = [5_000, 30_000, 120_000];

const REFUSED: InternalApiKeyAccess = {
  allowed: false,
  surface_enabled: false,
  permissions: [],
  ip_optional_permissions: [],
  max_ttl_days: 0,
  rotation_grace_hours: 0,
};

// The allowlist is read at backend startup, so one ANSWER per user and session is enough — a refusal (401, 403,
// 404) included, or staff without `api_key:read` would ask again on every mount. Any other failure is not an
// answer: it is never cached, or a blip would hide the module until the app is reloaded.
const answers = new Map<string, InternalApiKeyAccess>();
const inflight = new Map<string, Promise<InternalApiKeyAccess | null>>();
// Bumped on logout, so a request that was in flight then (a 401 from the expired session) caches nothing.
let session = 0;

/** Forgets every answer; called on logout, or the same person signing back in would keep a stale refusal. */
export function resetInternalApiKeyAccess(): void {
  session += 1;
  answers.clear();
  inflight.clear();
}

/** The cached answer, or a request shared by everyone asking at once. Resolves null on failure. */
export function readInternalApiKeyAccess(userId: string): Promise<InternalApiKeyAccess | null> {
  const known = answers.get(userId);
  if (known) return Promise.resolve(known);
  let promise = inflight.get(userId);
  if (!promise) {
    const asked = session;
    const remember = (access: InternalApiKeyAccess) => {
      if (asked === session) answers.set(userId, access);
      return access;
    };
    promise = InternalApiKeysService.access()
      .then(remember)
      .catch((error: unknown) => {
        const status = HttpApiService.isAxiosError(error) ? error.response?.status : undefined;
        if (!HttpApiService.isAccessDeniedError(error) && status !== 404) return null;
        return remember(REFUSED);
      })
      .finally(() => {
        if (asked === session) inflight.delete(userId);
      });
    inflight.set(userId, promise);
  }
  return promise;
}

export interface UseInternalApiKeyAccessResult {
  allowed: boolean;
  access: InternalApiKeyAccess | null;
  isLoading: boolean;
  /** The last read failed without an answer; retries are scheduled, and `retry` asks at once. */
  failed: boolean;
  retry: () => void;
}

/** Whether the signed-in staff member is on `INTERNAL_API_KEY_ADMIN_EMAILS`. Members and failures read false. */
export function useInternalApiKeyAccess(): UseInternalApiKeyAccessResult {
  const { user } = useAuth();
  const userId = user?.user_type === UserType.INTERNAL ? user.id : null;
  const [read, setRead] = useState<{ userId: string; access: InternalApiKeyAccess | null } | null>(() => {
    const known = userId ? answers.get(userId) : undefined;
    return userId && known ? { userId, access: known } : null;
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    void readInternalApiKeyAccess(userId).then((access) => {
      if (cancelled) return;
      setRead({ userId, access });
      const delay = RETRY_DELAYS_MS[attempt];
      if (!access && delay !== undefined) retry = setTimeout(() => setAttempt((n) => n + 1), delay);
    });
    return () => {
      cancelled = true;
      if (retry) clearTimeout(retry);
    };
  }, [userId, attempt]);

  const retry = () => setAttempt((n) => n + 1);
  if (!userId) return { allowed: false, access: null, isLoading: false, failed: false, retry };
  if (read?.userId !== userId) return { allowed: false, access: null, isLoading: true, failed: false, retry };
  return {
    allowed: read.access?.allowed === true,
    access: read.access,
    isLoading: false,
    failed: read.access === null,
    retry,
  };
}
